import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Download, 
  Smartphone, 
  Monitor, 
  X, 
  Bell,
  Check,
  Share
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/axios';

// =============================================
// INSTALL APP BUTTON WITH NOTIFICATION BADGE
// PWA Installation + Pending actions indicator
// Global PWA prompt management
// =============================================

const InstallAppButton = ({ className, showBadge = true, variant = 'default' }) => {
  const { isAuthenticated } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Detect device type - using useMemo to avoid re-renders
  const deviceInfo = useMemo(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return {
      isIOS: /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream,
      isAndroid: /android/i.test(userAgent),
      isSafari: /^((?!chrome|android).)*safari/i.test(userAgent),
      isChrome: /chrome/i.test(userAgent) && !/edge/i.test(userAgent)
    };
  }, []);

  // Check if already installed - calculated once
  const isInstalled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  }, []);

  // Listen for PWA install prompt - GLOBAL handler
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      // Store globally so Sidebar can access it
      window.deferredPrompt = e;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Listen for custom event to show modal (from Sidebar)
    const handleShowModal = () => setShowModal(true);
    window.addEventListener('show-pwa-modal', handleShowModal);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('show-pwa-modal', handleShowModal);
    };
  }, []);

  // Fetch pending actions count
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchPendingCount = async () => {
      try {
        const res = await api.get('/notifications/pending-count');
        setPendingCount(res.data?.total || 0);
      } catch (error) {
        // Fallback: try to calculate from different sources
        try {
          const [ticketsRes, validationsRes] = await Promise.all([
            api.get('/tickets?status=open&limit=1').catch(() => ({ data: [] })),
            api.get('/tasks/pending-validation?limit=1').catch(() => ({ data: [] }))
          ]);
          
          const total = 
            (Array.isArray(ticketsRes.data) ? ticketsRes.data.length : 0) +
            (Array.isArray(validationsRes.data) ? validationsRes.data.length : 0);
          
          setPendingCount(total > 99 ? 99 : total);
        } catch (e) {
          setPendingCount(0);
        }
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000); // Refresh every minute
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Handle install click
  const handleInstallClick = async () => {
    if (window.deferredPrompt) {
      try {
        window.deferredPrompt.prompt();
        const { outcome } = await window.deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          window.deferredPrompt = null;
        }
      } catch (e) {
        console.log('Install prompt error:', e);
        setShowModal(true);
      }
    } else {
      // No prompt available (iOS or already dismissed) - show instructions
      setShowModal(true);
    }
  };
  
  // Check if installable (has deferred prompt or is iOS/Android)
  const canShowButton = !isInstalled && (deviceInfo.isIOS || deviceInfo.isAndroid || typeof window !== 'undefined');

  // Don't show if already installed
  if (isInstalled || !canShowButton) {
    return null;
  }

  // Get icon based on device
  const DeviceIcon = deviceInfo.isIOS || deviceInfo.isAndroid ? Smartphone : Monitor;
  
  // Check if native install is available
  const hasNativeInstall = !!window.deferredPrompt;

  return (
    <>
      <Button
        variant={variant === 'sidebar' ? 'ghost' : 'outline'}
        size="sm"
        onClick={handleInstallClick}
        className={cn(
          "relative gap-2 transition-all",
          variant === 'sidebar' && "w-full justify-start text-white/70 hover:text-white hover:bg-white/10",
          className
        )}
        data-testid="install-app-btn"
      >
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">Installer l&apos;app</span>
        
        {/* Notification Badge */}
        {showBadge && pendingCount > 0 && (
          <Badge 
            className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold animate-pulse"
            data-testid="pending-notifications-badge"
          >
            {pendingCount}
          </Badge>
        )}
      </Button>

      {/* Installation Instructions Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DeviceIcon className="w-5 h-5 text-indigo-500" />
              Installer MSC Manager
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Pending notifications info */}
            {pendingCount > 0 && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <Bell className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-800">
                    {pendingCount} action{pendingCount > 1 ? 's' : ''} en attente
                  </p>
                  <p className="text-sm text-amber-600">
                    Installez l&apos;app pour recevoir des notifications
                  </p>
                </div>
              </div>
            )}

            {/* iOS Instructions */}
            {deviceInfo.isIOS && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Pour installer sur votre iPhone/iPad :
                </p>
                <div className="space-y-2">
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">1</div>
                    <div>
                      <p className="font-medium text-slate-800">Appuyez sur le bouton Partager</p>
                      <Share className="w-5 h-5 text-slate-400 mt-1" />
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">2</div>
                    <p className="font-medium text-slate-800">Sélectionnez &quot;Sur l&apos;écran d&apos;accueil&quot;</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">3</div>
                    <p className="font-medium text-slate-800">Appuyez sur &quot;Ajouter&quot;</p>
                  </div>
                </div>
              </div>
            )}

            {/* Android Instructions */}
            {deviceInfo.isAndroid && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Pour installer sur votre Android :
                </p>
                <div className="space-y-2">
                  {hasNativeInstall ? (
                    <Button 
                      onClick={handleInstallClick} 
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Installer maintenant
                    </Button>
                  ) : (
                    <>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">1</div>
                        <p className="font-medium text-slate-800">Ouvrez le menu (⋮) de Chrome</p>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">2</div>
                        <p className="font-medium text-slate-800">Sélectionnez &quot;Ajouter à l&apos;écran d&apos;accueil&quot;</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Desktop Instructions */}
            {!deviceInfo.isIOS && !deviceInfo.isAndroid && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Pour installer sur votre ordinateur :
                </p>
                <div className="space-y-2">
                  {hasNativeInstall ? (
                    <Button 
                      onClick={handleInstallClick} 
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Installer maintenant
                    </Button>
                  ) : (
                    <>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">1</div>
                        <p className="font-medium text-slate-800">Cherchez l&apos;icône d&apos;installation dans la barre d&apos;adresse</p>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">2</div>
                        <p className="font-medium text-slate-800">Ou utilisez le menu du navigateur → &quot;Installer...&quot;</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-slate-700 mb-2">Avantages de l&apos;application :</p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Accès rapide depuis l&apos;écran d&apos;accueil
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Notifications pour les actions en attente
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Expérience plein écran optimisée
                </li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
